<template>
  <section class="page" data-testid="page-observability">
    <header class="page-header">
      <div><h1>监控大盘</h1><p>QQ 消息、Pipeline、LLM、TTS、存储和 Trace。</p></div>
      <div class="actions">
        <button class="button" @click="toggleAutoRefresh">{{ autoRefresh ? "暂停自动刷新" : "开启自动刷新" }}</button>
        <button class="button" :disabled="!dashboard" @click="copySnapshot">复制诊断快照</button>
        <button class="button primary" :disabled="loading" @click="() => load()">{{ loading ? "刷新中..." : "刷新" }}</button>
      </div>
    </header>
    <div class="metrics-grid">
      <MetricCard label="健康" :value="healthStatus" />
      <MetricCard label="OneBot" :value="onebotStatusLabel" />
      <MetricCard label="入站健康" :value="onebotIngressLabel" />
      <MetricCard label="回复就绪" :value="onebotReplyReadyLabel" />
      <MetricCard label="数据库" :value="databaseStatus" />
      <MetricCard label="QQ 入站" :value="qq.totals?.inbound || 0" />
      <MetricCard label="QQ 出站" :value="qq.totals?.outbound || 0" />
      <MetricCard label="回复率" :value="percent(qq.totals?.reply_rate)" />
      <MetricCard label="最近消息匹配" :value="filteredMessages.length" />
      <MetricCard label="Trace 匹配" :value="filteredTraces.length" />
      <MetricCard label="人设" :value="domain.personas || 0" />
      <MetricCard label="音色" :value="domain.voices || 0" />
      <MetricCard label="表情包" :value="domain.stickers || 0" />
      <MetricCard label="发送计划" :value="delivery.summary?.recent_count || 0" />
      <MetricCard label="Stale 记录" :value="delivery.summary?.stale_count || 0" />
      <MetricCard label="跳过计划" :value="delivery.summary?.skipped_count || 0" />
      <MetricCard label="平均发送等待" :value="`${Math.round(delivery.summary?.avg_actual_delay_ms || 0)}ms`" />
      <MetricCard label="学习目标" :value="learning.summary?.target_count || 0" />
      <MetricCard label="待入 RAG" :value="learning.summary?.pending_rag_messages || 0" />
      <MetricCard label="学习停滞" :value="learning.summary?.stale_profiles || 0" />
      <MetricCard label="复读拦截" :value="replyNovelty.skipped || 0" />
      <MetricCard label="相似警告" :value="replyNovelty.warned || 0" />
      <MetricCard label="情绪" :value="personality.emotion?.style || '-'" />
      <MetricCard label="在场感" :value="personality.presence?.enabled === false ? '关闭' : '开启'" />
      <MetricCard label="主动发言" :value="personality.proactive?.enabled ? '开启' : '关闭'" />
      <MetricCard label="最近主动" :value="lastProactiveSend ? proactiveSendStatusLabel(lastProactiveSend.status) : '-'" />
      <MetricCard label="最近刷新" :value="lastLoadedAt ? lastLoadedAt.toLocaleTimeString('zh-CN') : '-'" />
    </div>
    <p v-if="error" class="error-text">{{ error }}</p>
    <p v-if="actionResult" class="save-result">{{ actionResult }}</p>

    <div class="panel">
      <h2>运行结论</h2>
      <div class="diagnostic-list">
        <div v-for="item in diagnostics" :key="item.label" class="diagnostic-row" :class="item.level">
          <strong>{{ item.label }}</strong>
          <span>{{ item.detail }}</span>
        </div>
      </div>
    </div>

    <div class="panel">
      <h2>OneBot 连接</h2>
      <p class="runtime-summary" :class="onebotIngressClass">
        <strong>{{ onebotIngressLabel }}</strong>
        <span>{{ onebotIngressSummary }}</span>
      </p>
      <div class="actions compact-actions">
        <button class="button" :disabled="onebotReconnecting || !dashboard" @click="reconnectOneBot">{{ onebotReconnecting ? "重连中..." : "重连 OneBot" }}</button>
      </div>
      <div class="runtime-grid compact">
        <div><span>状态</span><strong>{{ onebotStatusLabel }}</strong></div>
        <div><span>诊断</span><strong>{{ onebotDiagnosisLabel(onebotRuntime.diagnosis) }}</strong></div>
        <div><span>入站等级</span><strong>{{ onebotIngressLabel }}</strong></div>
        <div><span>回复就绪</span><strong>{{ onebotReplyReadyLabel }}</strong></div>
        <div><span>回复阻断</span><strong>{{ onebotReplyBlockerLabel }}</strong></div>
        <div><span>消息事件数</span><strong>{{ onebotRuntime.message_event_count ?? 0 }}</strong></div>
        <div><span>连接时长</span><strong>{{ formatSeconds(onebotRuntime.connection_uptime_seconds) }}</strong></div>
        <div><span>距最近事件</span><strong>{{ formatSeconds(onebotRuntime.event_idle_seconds) }}</strong></div>
        <div><span>距最近消息</span><strong>{{ formatSeconds(onebotRuntime.message_event_idle_seconds) }}</strong></div>
        <div><span>首条消息</span><strong>{{ formatTime(onebotRuntime.first_message_event_at) }}</strong></div>
        <div><span>最近消息</span><strong>{{ formatTime(onebotRuntime.last_message_event_at) }}</strong></div>
        <div><span>最近类型</span><strong>{{ onebotRuntime.last_message_type || "-" }}</strong></div>
        <div><span>重连</span><strong>{{ onebotRuntime.reconnects ?? 0 }}</strong></div>
        <div><span>断开</span><strong>{{ onebotRuntime.disconnect_count ?? 0 }}</strong></div>
        <div><span>待返回 API</span><strong>{{ onebotRuntime.pending_api_calls ?? 0 }}</strong></div>
        <div><span>消息处理</span><strong>{{ onebotRuntime.pending_handler_tasks ?? 0 }}</strong></div>
        <div><span>入站宽限</span><strong>{{ formatSeconds(onebotRuntime.ingress_warning_seconds) }}</strong></div>
        <div><span>首条探测</span><strong>{{ formatProbeStatus(onebotRuntime) }}</strong></div>
        <div><span>静默重连</span><strong>{{ Number(onebotRuntime.silent_reconnect_seconds || 0) > 0 ? formatSeconds(onebotRuntime.silent_reconnect_seconds) : "关闭" }}</strong></div>
        <div><span>当前阈值</span><strong>{{ onebotRuntime.auto_reconnect_enabled ? formatSeconds(onebotRuntime.active_silent_reconnect_seconds) : "关闭" }}</strong></div>
        <div><span>自愈原因</span><strong>{{ reconnectReasonLabel(onebotRuntime.active_silent_reconnect_reason) }}</strong></div>
        <div><span>下次自愈</span><strong>{{ onebotRuntime.auto_reconnect_enabled ? formatSeconds(onebotRuntime.next_reconnect_in_seconds) : "关闭" }}</strong></div>
        <div><span>自愈次数</span><strong>{{ onebotRuntime.silent_reconnect_count ?? 0 }}</strong></div>
        <div><span>手动重连</span><strong>{{ onebotRuntime.manual_reconnect_count ?? 0 }}</strong></div>
        <div><span>上次连接</span><strong>{{ formatTime(onebotRuntime.connected_at) }}</strong></div>
        <div><span>上次断开</span><strong>{{ formatTime(onebotRuntime.last_disconnect_at) }}</strong></div>
        <div><span>上次自愈</span><strong>{{ formatTime(onebotRuntime.last_silent_reconnect_at) }}</strong></div>
        <div><span>上次手动</span><strong>{{ formatTime(onebotRuntime.last_manual_reconnect_at) }}</strong></div>
      </div>
      <p v-if="onebotRuntime.last_message_preview" class="runtime-preview">最近入站：{{ onebotRuntime.last_message_preview }}</p>
      <p v-if="onebotIngressSuggestion" class="runtime-preview warning-text">{{ onebotIngressSuggestion }}</p>
    </div>

    <div class="panel">
      <h2>不回复复盘</h2>
      <div class="form-grid">
        <FormField label="开始时间"><input v-model="noReplyForm.since" placeholder="2026-07-05 16:16:30" /></FormField>
        <FormField label="结束时间"><input v-model="noReplyForm.until" placeholder="2026-07-05 16:19:30" /></FormField>
        <FormField label="群号"><input v-model="noReplyForm.group_id" placeholder="可选" /></FormField>
        <FormField label="用户 QQ"><input v-model="noReplyForm.user_id" placeholder="可选" /></FormField>
        <FormField label="关键词"><input v-model="noReplyForm.contains" placeholder="/persona 或消息片段" /></FormField>
        <label class="check-row"><input v-model="noReplyForm.include_napcat" type="checkbox" /> 对照 NapCat 日志</label>
      </div>
      <FormField label="粘贴消息流或 NapCat 日志">
        <textarea v-model="noReplyForm.napcat_log_text" rows="4" placeholder="可粘贴最近 QQ 消息流表格，或 docker compose logs napcat 片段"></textarea>
      </FormField>
      <div class="actions compact-actions">
        <button class="button" @click="fillNoReplyFromRecent">使用最近消息窗口</button>
        <button class="button" :disabled="!noReplyForm.napcat_log_text.trim()" @click="usePastedNoReplyWindow">使用粘贴时间</button>
        <button class="button" :disabled="!noReplyCanReconnect || onebotReconnecting" @click="reconnectOneBot">{{ onebotReconnecting ? "重连中..." : "按诊断重连 OneBot" }}</button>
        <button class="button" :disabled="!noReplyTraceId" @click="focusNoReplyTrace">查看诊断 Trace</button>
        <button class="button" :disabled="!noReplyReport" @click="copyNoReplyReport">复制诊断 JSON</button>
        <button class="button primary" :disabled="noReplyLoading" @click="runNoReplyDiagnostic">{{ noReplyLoading ? "诊断中..." : "诊断窗口" }}</button>
      </div>
      <div v-if="noReplyReport" class="diagnostic-list">
        <div class="diagnostic-row" :class="noReplyLevelClass(noReplyReport.diagnosis.level)">
          <strong>{{ noReplyCodeLabel(noReplyReport.diagnosis.code) }}</strong>
          <span>{{ noReplyReport.diagnosis.summary }}</span>
        </div>
        <div class="diagnostic-row warning">
          <strong>下一步</strong>
          <span>{{ noReplyReport.diagnosis.next_action }}</span>
        </div>
      </div>
      <div v-if="noReplyReport" class="runtime-grid compact">
        <div><span>NapCat 接收</span><strong>{{ noReplyReport.napcat.received_count }}</strong></div>
        <div><span>NapCat 发送</span><strong>{{ noReplyReport.napcat.sent_count }}</strong></div>
        <div><span>bot 入站</span><strong>{{ noReplyReport.bot.inbound.length }}</strong></div>
        <div><span>bot 出站</span><strong>{{ noReplyReport.bot.outbound.length }}</strong></div>
        <div><span>Trace</span><strong>{{ noReplyReport.bot.traces.length }}</strong></div>
        <div><span>窗口</span><strong>{{ formatTime(noReplyReport.window.since_ts) }} - {{ formatTime(noReplyReport.window.until_ts) }}</strong></div>
      </div>
      <p v-if="noReplyReport?.diagnosis.detail" class="runtime-preview">{{ noReplyReport.diagnosis.detail }}</p>
      <div v-if="noReplySamples.length" class="trace-detail compact-detail">
        <div class="detail-head"><strong>证据样例</strong></div>
        <pre>{{ noReplySamples.join("\n") }}</pre>
      </div>
    </div>

    <div class="split">
      <div class="panel">
        <h2>人格运行态</h2>
        <div class="runtime-grid">
          <div><span>情绪更新</span><strong>{{ personality.emotion?.update_on_message === false ? "关闭" : "开启" }}</strong></div>
          <div><span>Prompt 注入</span><strong>{{ personality.emotion?.prompt_enabled === false ? "关闭" : "开启" }}</strong></div>
          <div><span>风格修正</span><strong>{{ personality.emotion?.style_modifiers_enabled === false ? "关闭" : "开启" }}</strong></div>
          <div><span>在场概率下限</span><strong>{{ formatNumber(personality.presence?.min_reply_probability) }}</strong></div>
          <div><span>在场概率上限</span><strong>{{ formatNumber(personality.presence?.max_reply_probability) }}</strong></div>
          <div><span>喊名字加成</span><strong>{{ formatNumber(personality.presence?.bot_name_boost) }}</strong></div>
          <div><span>问句加成</span><strong>{{ formatNumber(personality.presence?.direct_question_boost) }}</strong></div>
          <div><span>高能加成</span><strong>{{ formatNumber(personality.presence?.high_energy_boost) }}</strong></div>
          <div><span>刚发言倍率</span><strong>{{ formatNumber(personality.presence?.recent_reply_multiplier) }}</strong></div>
          <div><span>喊名接话</span><strong>{{ personality.presence?.interrupt_bypass_on_name_mention === false ? "关闭" : "开启" }}</strong></div>
          <div><span>兜底概率</span><strong>{{ formatNumber(personality.presence?.fallback_probability) }}</strong></div>
          <div><span>主动群数</span><strong>{{ personality.proactive?.groups?.length || 0 }}</strong></div>
          <div><span>主动概率</span><strong>{{ formatNumber(personality.proactive?.probability) }}</strong></div>
          <div><span>主动间隔</span><strong>{{ personality.proactive?.min_interval_minutes || "-" }}m</strong></div>
          <div><span>主动阈值</span><strong>{{ personality.proactive?.activity_threshold || "-" }}</strong></div>
          <div><span>愉悦</span><strong>{{ formatNumber(personality.emotion?.state?.valence) }}</strong></div>
          <div><span>兴奋</span><strong>{{ formatNumber(personality.emotion?.state?.arousal) }}</strong></div>
        </div>
      </div>
      <div class="panel">
        <h2>跳过原因</h2>
        <DataTable :columns="reasonColumns" :rows="qq.by_stop_reason || []">
          <template #key="{ row }">
            <div class="reason-cell">
              <span class="status-badge" :class="stopReasonClass(row.key)">{{ reasonLabel(row.key) }}</span>
              <small>{{ reasonDetail(row.key) }}</small>
            </div>
          </template>
          <template #value="{ row }">{{ row.value }}</template>
        </DataTable>
      </div>
      <div class="panel">
        <h2>消息状态</h2>
        <DataTable :columns="messageStatusColumns" :rows="qq.by_status || []">
          <template #key="{ row }">
            <span class="status-badge" :class="messageStatusClass(row.key)">{{ messageStatusLabel(row.key) }}</span>
          </template>
          <template #value="{ row }">{{ row.value }}</template>
        </DataTable>
      </div>
    </div>
    <div class="panel">
      <h2>发送节奏</h2>
      <div class="toolbar">
        <button class="button" :class="{ primary: deliveryFilter === 'all' }" @click="deliveryFilter = 'all'">全部</button>
        <button class="button" :class="{ primary: deliveryFilter === 'sent' }" @click="deliveryFilter = 'sent'">已发送</button>
        <button class="button" :class="{ primary: deliveryFilter === 'pending' }" @click="deliveryFilter = 'pending'">待发送</button>
        <button class="button" :class="{ primary: deliveryFilter === 'stale' }" @click="deliveryFilter = 'stale'">Stale</button>
      </div>
      <DataTable :columns="deliveryColumns" :rows="filteredDeliveryPlans">
        <template #plan_units="{ row }">{{ (row.plan_units || []).map((u: any) => u.unit_type).join(",") }}</template>
        <template #human_summary="{ row }">{{ row.human_summary || "-" }}</template>
        <template #estimated_delay_ms="{ row }">{{ Math.round(row.estimated_delay_ms || 0) }}ms</template>
        <template #actual_delay_ms="{ row }">{{ Math.round(row.actual_delay_ms || 0) }}ms</template>
        <template #stale="{ row }"><span class="status-badge" :class="row.stale ? 'degraded' : 'ok'">{{ row.stale ? row.stale_reason || "是" : "否" }}</span></template>
      </DataTable>
    </div>
    <div class="panel">
      <h2>主动发言诊断</h2>
      <div class="runtime-grid compact">
        <div><span>真实触发</span><strong>{{ proactiveRuntime.trigger_count || 0 }}</strong></div>
        <div><span>已用记忆</span><strong>{{ proactiveRuntime.used_memory_count || 0 }}</strong></div>
        <div><span>最近跳过</span><strong>{{ topSkipReason }}</strong></div>
        <div><span>最近主动</span><strong>{{ lastProactiveSend ? proactiveSendStatusLabel(lastProactiveSend.status) : "-" }}</strong></div>
        <div><span>主动策略</span><strong>{{ lastProactiveSend ? proactiveStrategyLabel(lastProactiveSend.strategy) : "-" }}</strong></div>
      </div>
      <DataTable :columns="proactiveColumns" :rows="proactiveRuntime.groups_status || []" row-key="group_id">
        <template #current_summary="{ row }">{{ row.current_summary || reasonLabel(row.current_reason) }}</template>
        <template #eligible_now="{ row }">{{ row.eligible_now ? "满足" : "未满足" }}</template>
        <template #message_count="{ row }">{{ row.message_count || 0 }}/{{ row.activity_threshold || "-" }}</template>
        <template #weighted_probability="{ row }">{{ formatNumber(row.weighted_probability) }}</template>
        <template #seconds_since_bot_speak="{ row }">{{ formatSeconds(row.seconds_since_bot_speak) }}</template>
        <template #last_check="{ row }">{{ row.last_check ? reasonLabel(row.last_check.reason) : "未检查" }}</template>
      </DataTable>
      <h3>最近主动尝试</h3>
      <DataTable :columns="proactiveSendColumns" :rows="proactiveRuntime.recent_sends || []">
        <template #recorded_at="{ row }">{{ formatTime(row.recorded_at) }}</template>
        <template #strategy="{ row }">{{ proactiveStrategyLabel(row.strategy) }}</template>
        <template #status="{ row }"><span class="status-badge" :class="proactiveSendStatusClass(row.status)">{{ proactiveSendStatusLabel(row.status) }}</span></template>
        <template #used_memory="{ row }">{{ row.used_memory ? "使用记忆" : "未用记忆" }}</template>
        <template #delivery="{ row }">{{ formatProactiveDelivery(row.delivery) }}</template>
        <template #reply_preview="{ row }">{{ row.error || row.reply_preview || "-" }}</template>
      </DataTable>
    </div>
    <div class="panel">
      <h2>学习诊断</h2>
      <DataTable :columns="learningColumns" :rows="learning.profiles || []" row-key="persona">
        <template #enabled="{ row }">{{ row.enabled === false ? "暂停" : "开启" }}</template>
        <template #targets="{ row }">{{ (row.qq_ids || []).length }} QQ / {{ (row.groups || []).length }} 群</template>
        <template #progress="{ row }">
          RAG {{ row.progress?.messages_until_rag ?? "-" }} / 表达 {{ row.progress?.messages_until_expression ?? "-" }} / 风格 {{ row.progress?.messages_until_style_update ?? "-" }}
        </template>
        <template #stale="{ row }">{{ row.stale ? "停滞" : "正常" }}</template>
      </DataTable>
    </div>
    <div class="panel">
      <h2>最近 QQ 消息流</h2>
      <p class="hint-text">{{ qqRecentSourceText }}</p>
      <div class="toolbar">
        <input v-model="messageQuery" placeholder="搜索会话、昵称、内容、停止原因" />
        <button class="button" :class="{ primary: messageFilter === 'all' }" @click="messageFilter = 'all'">全部</button>
        <button class="button" :class="{ primary: messageFilter === 'inbound' }" @click="messageFilter = 'inbound'">入站</button>
        <button class="button" :class="{ primary: messageFilter === 'outbound' }" @click="messageFilter = 'outbound'">出站</button>
        <button class="button" :class="{ primary: messageFilter === 'stopped' }" @click="messageFilter = 'stopped'">已停止</button>
      </div>
      <DataTable :columns="messageColumns" :rows="filteredMessages">
        <template #ts="{ row }">{{ formatTime(row.ts) }}</template>
        <template #direction="{ row }">{{ formatDirection(row.direction) }}</template>
        <template #segment_types="{ row }">{{ (row.segment_types || []).join(",") }}</template>
        <template #reply_delivery="{ row }">{{ formatReplyDelivery(row.reply_delivery, row.delivery_type) }}</template>
        <template #diagnosis="{ row }">{{ formatMessageDiagnosis(row) }}</template>
        <template #action="{ row }">
          <button class="button small" :disabled="row.direction !== 'in' || !row.ts || noReplyLoading" @click="diagnoseNoReplyMessage(row)">
            复盘
          </button>
        </template>
        <template #stop_reason="{ row }">
          <div v-if="row.stop_reason" class="reason-cell">
            <span class="status-badge" :class="stopReasonClass(row.stop_reason)">{{ reasonLabel(row.stop_reason) }}</span>
            <small>{{ reasonDetail(row.stop_reason) }}</small>
          </div>
          <span v-else>-</span>
        </template>
      </DataTable>
    </div>
    <div class="split">
      <div class="panel">
        <h2>Trace</h2>
        <div class="toolbar">
          <input v-model="traceQuery" placeholder="搜索 trace、会话、用户" />
          <button class="button" :class="{ primary: traceFilter === 'all' }" @click="traceFilter = 'all'">全部</button>
          <button class="button" :class="{ primary: traceFilter === 'dry-run' }" @click="traceFilter = 'dry-run'">Dry Run</button>
          <button class="button" :class="{ primary: traceFilter === 'live' }" @click="traceFilter = 'live'">真实</button>
        </div>
        <DataTable :columns="traceColumns" :rows="filteredTraces" row-key="trace_id">
          <template #dry_run="{ row }">{{ row.dry_run ? "是" : "否" }}</template>
          <template #final_decision="{ row }">{{ formatTraceDecision(row.final_decision) }}</template>
          <template #action="{ row }">
            <div class="row-actions">
              <button class="button small" @click="selectedTrace = row">查看</button>
              <button class="button small" @click="copyTrace(row)">复制</button>
            </div>
          </template>
        </DataTable>
      </div>
      <div class="panel">
        <h2>Trace 详情</h2>
        <div v-if="selectedTrace" class="trace-detail">
          <div class="detail-head">
            <strong>{{ selectedTrace.trace_id || "-" }}</strong>
            <button class="button small" @click="copyTrace(selectedTrace)">复制 JSON</button>
          </div>
          <div class="runtime-grid compact">
            <div><span>Dry Run</span><strong>{{ selectedTrace.dry_run ? "是" : "否" }}</strong></div>
            <div><span>会话</span><strong>{{ selectedTrace.context_key_hash || "-" }}</strong></div>
            <div><span>用户</span><strong>{{ selectedTrace.user_hash || "-" }}</strong></div>
            <div><span>阶段</span><strong>{{ (selectedTrace.stages || []).length }}</strong></div>
            <div><span>停止原因</span><strong>{{ selectedTrace.final_decision?.stop_reason || "-" }}</strong></div>
            <div><span>投递</span><strong>{{ formatReplyDelivery(selectedTrace.final_decision?.reply_delivery, selectedTrace.final_decision?.delivery_type) }}</strong></div>
            <div><span>发送节奏</span><strong>{{ formatTraceDeliveryTiming(selectedTrace.delivery_timing) }}</strong></div>
            <div><span>策略结论</span><strong>{{ formatTraceDecision(selectedTrace.final_decision) }}</strong></div>
          </div>
          <div v-if="selectedFinalDecision" class="trace-pills">
            <span v-if="selectedFinalDecision.presence">{{ formatPresenceDecision(selectedFinalDecision.presence) }}</span>
            <span v-if="selectedFinalDecision.relationship">{{ formatRelationshipDecision(selectedFinalDecision.relationship) }}</span>
            <span v-if="selectedFinalDecision.user_profile">{{ formatUserProfileDecision(selectedFinalDecision.user_profile) }}</span>
            <span v-if="selectedFinalDecision.prompt_context">{{ formatPromptContextDecision(selectedFinalDecision.prompt_context) }}</span>
            <span v-if="selectedFinalDecision.emotion">{{ formatEmotionDecision(selectedFinalDecision.emotion) }}</span>
            <span v-if="selectedFinalDecision.memory_recall">{{ formatMemoryRecallDecision(selectedFinalDecision.memory_recall) }}</span>
            <span v-if="selectedFinalDecision.reply_novelty">{{ formatReplyNoveltyDecision(selectedFinalDecision.reply_novelty) }}</span>
            <span v-if="selectedFinalDecision.sticker_decision">{{ formatStickerDecision(selectedFinalDecision.sticker_decision) }}</span>
            <span v-if="selectedFinalDecision.taboo">{{ formatTabooDecision(selectedFinalDecision.taboo) }}</span>
            <span v-if="selectedFinalDecision.multimodal">{{ formatMultimodalDecision(selectedFinalDecision.multimodal) }}</span>
            <span v-if="formatBurstMergeDecision(selectedFinalDecision.burst_merge) !== '-'">{{ formatBurstMergeDecision(selectedFinalDecision.burst_merge) }}</span>
          </div>
          <pre>{{ JSON.stringify(selectedTrace, null, 2) }}</pre>
        </div>
        <div v-else class="empty">选择一条 trace 查看完整上下文。</div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { api, postJson } from "../api/client";
import type {
  DashboardPayload,
  NoReplyDiagnosticPayload,
  NoReplyDiagnosticReport,
  NoReplyDiagnosticRequest,
  OneBotRuntimePayload
} from "../api/types";
import { useClipboardResult } from "../composables/useClipboardResult";
import { useOneBotReconnect } from "../composables/useOneBotReconnect";
import {
  formatStopReason,
  reasonDetail,
  reasonLabel,
  reasonSearchText,
  stopReasonClass
} from "../diagnostics/stopReasons";
import {
  onebotDiagnosisLabel,
  onebotIngressLevelClass,
  onebotIngressLevelLabel,
  onebotIngressReason,
  onebotIngressSummary as formatOnebotIngressSummary,
  onebotRecoveryHint,
  onebotReplyReadyLabel as formatOnebotReplyReadyLabel,
  onebotStateLabel
} from "../diagnostics/onebot";
import { useToastStore } from "../stores/toast";
import DataTable from "../ui/DataTable.vue";
import FormField from "../ui/FormField.vue";
import MetricCard from "../ui/MetricCard.vue";

const toast = useToastStore();
const dashboard = ref<DashboardPayload | null>(null);
const loading = ref(false);
const error = ref("");
const actionResult = ref("");
const { copyJson: copySnapshotJson } = useClipboardResult(actionResult, "浏览器不允许复制，请手动选择 trace 或原始数据");
const { copyJson: copyTraceJson } = useClipboardResult(actionResult, "浏览器不允许复制，请手动选择 trace 详情");
const { copyJson: copyNoReplyJson } = useClipboardResult(actionResult, "浏览器不允许复制，请手动选择诊断结果");
const { reconnecting: onebotReconnecting, reconnectOneBot } = useOneBotReconnect({
  actionResult,
  error,
  onSuccess() {
    window.setTimeout(() => load(true), 1800);
  }
});

const autoRefresh = ref(false);
const lastLoadedAt = ref<Date | null>(null);
const messageQuery = ref("");
const messageFilter = ref<"all" | "inbound" | "outbound" | "stopped">("all");
const traceQuery = ref("");
const traceFilter = ref<"all" | "dry-run" | "live">("all");
const deliveryFilter = ref<"all" | "sent" | "pending" | "stale">("all");
const selectedTrace = ref<any | null>(null);
const noReplyLoading = ref(false);
const noReplyReport = ref<NoReplyDiagnosticReport | null>(null);
const noReplyForm = ref({
  since: defaultLocalTime(-10 * 60),
  until: defaultLocalTime(0),
  group_id: "",
  user_id: "",
  contains: "",
  include_napcat: true,
  napcat_log_text: ""
});
const noReplyCanReconnect = computed(() => {
  return noReplyReport.value?.diagnosis.recovery_action === "reconnect_onebot";
});
const noReplyTraceId = computed(() => {
  const traces = noReplyReport.value?.bot.traces || [];
  const trace = traces.find((item: any) => item.trace_id);
  return trace?.trace_id ? String(trace.trace_id) : "";
});
let timer: number | undefined;
const qq = computed(() => dashboard.value?.qq || {});
const domain = computed(() => dashboard.value?.domain || {});
const delivery = computed(() => dashboard.value?.delivery_timing || {});
const learning = computed(() => dashboard.value?.learning || {});
const metricsDashboard = computed(() => ((dashboard.value?.metrics as any)?.dashboard || {}));
const replyNovelty = computed(() => metricsDashboard.value?.reply_novelty || {});
const personality = computed(() => dashboard.value?.personality || {});
const proactiveRuntime = computed(() => personality.value?.proactive?.runtime || {});
const lastProactiveSend = computed(() => proactiveRuntime.value?.last_send || null);
const healthStatus = computed(() => dashboard.value?.health?.status || "-");
const databaseStatus = computed(() => (dashboard.value?.storage as any)?.database?.status || "-");
const onebotRuntime = computed(() => {
  const statsRuntime = (dashboard.value?.stats as { onebot?: OneBotRuntimePayload } | undefined)?.onebot;
  const detailedRuntime = dashboard.value?.health?.components?.onebot?.runtime;
  return statsRuntime || detailedRuntime || dashboard.value?.health?.onebot || {};
});
const onebotConnected = computed(() => Boolean(
  onebotRuntime.value?.connected
  || (dashboard.value?.stats as { onebot_connected?: boolean } | undefined)?.onebot_connected
  || dashboard.value?.health?.onebot_connected
  || dashboard.value?.health?.onebot?.connected
));
const onebotStatusLabel = computed(() => {
  if (!dashboard.value) return "-";
  if (onebotConnected.value) return "已连接";
  const state = String(onebotRuntime.value?.state || "");
  return onebotStateLabel(state);
});
const onebotIngress = computed(() => onebotRuntime.value?.ingress_health || {});
const onebotIngressVerdict = computed(() => onebotRuntime.value?.ingress_verdict || {});
const onebotIngressLevel = computed(() => String(onebotIngress.value?.level || (onebotConnected.value ? "waiting" : "error")));
const onebotIngressLabel = computed(() => onebotIngressLevelLabel(onebotIngressLevel.value, onebotIngressReason(onebotRuntime.value, onebotIngress.value)));
const onebotIngressClass = computed(() => onebotIngressLevelClass(onebotIngressLevel.value));
const onebotIngressSummary = computed(() => formatOnebotIngressSummary(onebotRuntime.value, onebotConnected.value, onebotIngress.value, onebotIngressVerdict.value));
const onebotIngressSuggestion = computed(() => onebotRecoveryHint(onebotRuntime.value, onebotIngress.value));
const onebotReplyReadyLabel = computed(() => formatOnebotReplyReadyLabel(onebotRuntime.value));
const onebotReplyBlocker = computed(() => String(onebotRuntime.value?.reply_blocker || ""));
const onebotReplyBlockerLabel = computed(() => onebotReplyBlocker.value ? onebotDiagnosisLabel(onebotReplyBlocker.value) : "-");
const selectedFinalDecision = computed(() => {
  const value = selectedTrace.value?.final_decision;
  return value && Object.keys(value).length ? value : null;
});
const topSkipReason = computed(() => {
  const counts = proactiveRuntime.value?.skip_counts || {};
  const entries = Object.entries(counts).sort((a, b) => Number(b[1]) - Number(a[1]));
  return entries.length ? `${reasonLabel(entries[0][0])} ${entries[0][1]}` : "-";
});
const filteredDeliveryPlans = computed(() => {
  const plans = delivery.value?.recent_plans || [];
  return plans.filter((item: any) => {
    if (deliveryFilter.value === "stale") return !!item.stale;
    if (deliveryFilter.value === "sent") return item.status === "sent";
    if (deliveryFilter.value === "pending") return item.status && item.status !== "sent";
    return true;
  });
});
const filteredMessages = computed(() => {
  const text = messageQuery.value.trim().toLowerCase();
  return (qq.value?.recent || []).filter((item: any) => {
    if (messageFilter.value === "inbound" && item.direction !== "in") return false;
    if (messageFilter.value === "outbound" && item.direction !== "out") return false;
    if (messageFilter.value === "stopped" && !item.stop_reason) return false;
    if (!text) return true;
    return [
      item.chat_key,
      item.nickname,
      item.direction,
      item.status,
      item.stop_reason,
      reasonSearchText(item.stop_reason),
      item.delivery_type,
      JSON.stringify(item.reply_delivery || {}),
      JSON.stringify(item.trace_final_decision || {}),
      item.text_preview,
      (item.segment_types || []).join(" ")
    ].join(" ").toLowerCase().includes(text);
  });
});
const filteredTraces = computed(() => {
  const text = traceQuery.value.trim().toLowerCase();
  return (dashboard.value?.traces || []).filter((item: any) => {
    if (traceFilter.value === "dry-run" && !item.dry_run) return false;
    if (traceFilter.value === "live" && item.dry_run) return false;
    if (!text) return true;
    return [
      item.trace_id,
      item.context_key_hash,
      item.user_hash,
      JSON.stringify(item.stages || []),
      JSON.stringify(item.final_decision || {}),
      JSON.stringify(item.delivery_timing || {})
    ].join(" ").toLowerCase().includes(text);
  });
});
const noReplySamples = computed(() => {
  const report = noReplyReport.value;
  if (!report) return [];
  const samples: string[] = [];
  samples.push(...(report.napcat.sample_received || []).map((line) => `NapCat 收: ${line}`));
  samples.push(...(report.napcat.sample_sent || []).map((line) => `NapCat 发: ${line}`));
  samples.push(...(report.bot.inbound || []).slice(0, 3).map((item: any) => `bot 入站: ${item.text_preview || "-"} trace=${item.trace_id || "-"}`));
  samples.push(...(report.bot.outbound || []).slice(0, 3).map((item: any) => `bot 出站: ${item.text_preview || "-"}`));
  return samples.slice(0, 8);
});
const noReplyTraceFallback = computed(() => {
  const traceId = noReplyTraceId.value;
  if (!traceId) return null;
  return (noReplyReport.value?.bot.traces || []).find((item: any) => item.trace_id === traceId) || null;
});
const qqRecentSourceText = computed(() => {
  const source = qq.value?.recent_source || "memory";
  if (source === "database") {
    return `最近消息来自持久化数据库，显示 ${qq.value?.persistent_recent_count || 0} 条；实时窗口指标仍来自当前进程内存。`;
  }
  if (qq.value?.persistent_error) {
    return `最近消息来自当前进程内存；读取持久化记录失败：${qq.value.persistent_error}`;
  }
  return "最近消息来自当前进程内存；重启后会自动尝试从持久化数据库补齐列表。";
});
const diagnostics = computed(() => {
  const stalePlans = Number(delivery.value?.summary?.stale_count || 0);
  const skippedPlans = Number(delivery.value?.summary?.skipped_count || 0);
  const replyRate = Number(qq.value?.totals?.reply_rate || 0);
  const stoppedMessages = (qq.value?.recent || []).filter((item: any) => item.stop_reason).length;
  const staleProfiles = Number(learning.value?.summary?.stale_profiles || 0);
  const traceCount = dashboard.value?.traces?.length || 0;
  const components = dashboard.value?.health?.components || {};
  const database = (dashboard.value?.storage as any)?.database || {};
  const onebot = onebotRuntime.value || {};
  const stt = (components as any).stt || {};
  const vision = (components as any).vision || {};
  const multimodalIssues = [stt, vision].filter((item: any) => item.status && item.status !== "ok");
  const multimodalFix = multimodalIssues.map((item: any) => item.fix).filter(Boolean).join("；");
  return [
    {
      label: "系统健康",
      detail: dashboard.value ? `health=${healthStatus.value}，security=${dashboard.value.security?.status || "-"}` : "尚未加载 dashboard",
      level: healthStatus.value === "ok" ? "ok" : "error"
    },
    {
      label: "数据库",
      detail: database.status === "ok"
        ? `SQLite 正常，${Math.round(Number(database.bytes || 0) / 1024)}KB`
        : database.status === "unknown"
          ? database.detail || "数据库未挂载"
          : `${database.reason || "健康检查失败"}：${(database.findings || [database.error || ""])[0] || "查看后台日志"}`,
      level: database.status === "ok" ? "ok" : database.status === "unknown" ? "warning" : "error"
    },
    {
      label: "OneBot 入口",
      detail: onebotConnected.value
        ? `${onebotIngressLabel.value}：${onebotIngressSummary.value}；距最近消息 ${formatSeconds(onebot.message_event_idle_seconds)}，重连 ${onebot.reconnects || 0} 次`
        : `${onebotStateLabel(onebot.state)}：${onebotDiagnosisLabel(onebot.diagnosis)}`,
      level: onebotConnected.value ? onebotIngressLevelClass(onebotIngressLevel.value) : "error"
    },
    {
      label: "回复行为",
      detail: `回复率 ${percent(replyRate)}，最近停止 ${stoppedMessages} 条，复读拦截 ${replyNovelty.value.skipped || 0} 条`,
      level: replyRate > 0 || stoppedMessages === 0 ? "ok" : "warning"
    },
    {
      label: "发送节奏",
      detail: stalePlans
        ? `${stalePlans} 条发送计划 stale，已跳过 ${skippedPlans} 条`
        : "发送计划未发现 stale",
      level: stalePlans ? "warning" : "ok"
    },
    {
      label: "人格学习",
      detail: staleProfiles ? `${staleProfiles} 个人设学习停滞` : `待入 RAG ${learning.value?.summary?.pending_rag_messages || 0} 条`,
      level: staleProfiles ? "warning" : "ok"
    },
    {
      label: "多模态入口",
      detail: multimodalIssues.length
        ? `语音=${stt.status || "-"}，图片=${vision.status || "-"}；${multimodalFix || "检查健康组件详情"}`
        : `语音=${stt.status || "-"}，图片=${vision.status || "-"}`,
      level: multimodalIssues.length ? "warning" : "ok"
    },
    {
      label: "Trace 证据",
      detail: traceCount ? `${traceCount} 条最近 trace 可用于复盘` : "暂无 trace，先用调试页或真实消息触发一次",
      level: traceCount ? "ok" : "warning"
    }
  ];
});
const messageColumns = [
  { key: "ts", label: "时间" },
  { key: "direction", label: "方向" },
  { key: "chat_key", label: "会话" },
  { key: "nickname", label: "用户" },
  { key: "segment_types", label: "类型" },
  { key: "reply_delivery", label: "投递决策" },
  { key: "diagnosis", label: "诊断" },
  { key: "stop_reason", label: "停止原因" },
  { key: "text_preview", label: "内容" },
  { key: "action", label: "操作" }
];
const reasonColumns = [
  { key: "key", label: "原因" },
  { key: "value", label: "次数" }
];
const messageStatusColumns = [
  { key: "key", label: "状态" },
  { key: "value", label: "次数" }
];
const traceColumns = [
  { key: "trace_id", label: "Trace" },
  { key: "dry_run", label: "Dry Run" },
  { key: "context_key_hash", label: "会话" },
  { key: "user_hash", label: "用户" },
  { key: "final_decision", label: "决策" },
  { key: "action", label: "操作" }
];
const deliveryColumns = [
  { key: "priority", label: "优先级" },
  { key: "status", label: "状态" },
  { key: "plan_units", label: "单元" },
  { key: "human_summary", label: "说明" },
  { key: "estimated_delay_ms", label: "预计" },
  { key: "actual_delay_ms", label: "实际" },
  { key: "stale", label: "Stale" }
];
const proactiveColumns = [
  { key: "group_id", label: "群" },
  { key: "current_summary", label: "当前说明" },
  { key: "eligible_now", label: "硬门槛" },
  { key: "message_count", label: "活跃" },
  { key: "fresh_count", label: "新鲜话题" },
  { key: "weighted_probability", label: "概率" },
  { key: "seconds_since_bot_speak", label: "距发言" },
  { key: "last_check", label: "上次检查" },
  { key: "last_message_preview", label: "最近消息" }
];
const proactiveSendColumns = [
  { key: "recorded_at", label: "时间" },
  { key: "group_id", label: "群" },
  { key: "strategy", label: "策略" },
  { key: "status", label: "状态" },
  { key: "used_memory", label: "记忆" },
  { key: "delivery", label: "投递" },
  { key: "reply_preview", label: "预览" }
];
const learningColumns = [
  { key: "persona", label: "人设" },
  { key: "enabled", label: "状态" },
  { key: "targets", label: "目标" },
  { key: "total_learned", label: "已学" },
  { key: "buffer_size", label: "缓冲" },
  { key: "progress", label: "还差" },
  { key: "stale", label: "新鲜度" }
];
function formatLocalDateTime(date: Date) {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}
function defaultLocalTime(offsetSeconds: number) {
  return formatLocalDateTime(new Date(Date.now() + offsetSeconds * 1000));
}
function localTimeFromEpochSeconds(tsSeconds: number, offsetSeconds = 0) {
  return formatLocalDateTime(new Date((tsSeconds + offsetSeconds) * 1000));
}
function percent(v: number | undefined) { return `${Math.round((v || 0) * 1000) / 10}%`; }
function formatNumber(v: unknown) {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "-";
}
function formatSeconds(v: unknown) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  if (n > 86400) return ">1d";
  if (n > 3600) return `${Math.floor(n / 3600)}h`;
  if (n > 60) return `${Math.floor(n / 60)}m`;
  return `${Math.floor(n)}s`;
}
function reconnectReasonLabel(value: unknown) {
  const labels: Record<string, string> = {
    first_message_probe: "首条探测",
    first_message_grace: "首条宽限",
    long_silence: "长期静默",
    disabled: "关闭"
  };
  return labels[String(value || "")] || String(value || "-");
}
function formatProbeStatus(runtime: OneBotRuntimePayload) {
  const seconds = Number(runtime.first_message_probe_seconds || 0);
  const max = Number(runtime.first_message_probe_reconnects || 0);
  if (!seconds || !max) return "关闭";
  const used = Number(runtime.first_message_probe_reconnect_count || 0);
  return `${formatSeconds(seconds)} / ${used}/${max}`;
}
function formatDirection(value: unknown) {
  return String(value) === "in" ? "入站" : String(value) === "out" ? "出站" : String(value || "-");
}
function messageStatusLabel(value: unknown) {
  const labels: Record<string, string> = {
    ok: "正常",
    error: "异常"
  };
  return labels[String(value || "")] || String(value || "-");
}
function messageStatusClass(value: unknown) {
  const status = String(value || "");
  if (!status || status === "ok") return "ok";
  if (status === "error") return "down";
  return "degraded";
}
function formatReplyDelivery(value: unknown, fallbackType: unknown) {
  const payload = value && typeof value === "object" ? value as Record<string, any> : {};
  const parts: string[] = [];
  const deliveryType = payload.delivery_type || fallbackType;
  const pipelineError = formatPipelineError(payload.pipeline_error);
  if (pipelineError !== "-") parts.push(`异常:${pipelineError}`);
  const pipelineWarnings = formatPipelineWarnings(payload.pipeline_warnings);
  if (pipelineWarnings !== "-") parts.push(`降级:${pipelineWarnings}`);
  if (payload.skipped) {
    parts.push(`跳过:${reasonLabel(payload.skip_reason || "stale_skipped")}`);
  }
  if (deliveryType) parts.push(String(deliveryType));
  if (payload.quote) parts.push("引用");
  if (payload.part_count) parts.push(`${payload.part_count}段`);
  if (Array.isArray(payload.unit_types) && payload.unit_types.length) {
    parts.push(payload.unit_types.join("+"));
  }
  if (payload.used_recall) parts.push("撤回");
  if (payload.used_typo) parts.push("错字");
  if (payload.sticker_selected && payload.sticker_placement) {
    parts.push(String(payload.sticker_placement));
  }
  return parts.length ? parts.join(" / ") : "-";
}
function formatTraceDecision(value: unknown) {
  const payload = value && typeof value === "object" ? value as Record<string, any> : {};
  const parts: string[] = [];
  if (payload.stop_reason) parts.push(formatStopReason(payload.stop_reason));
  const pipelineError = formatPipelineError(payload.pipeline_error);
  if (pipelineError !== "-") parts.push(`异常:${pipelineError}`);
  const pipelineWarnings = formatPipelineWarnings(payload.pipeline_warnings);
  if (pipelineWarnings !== "-") parts.push(`降级:${pipelineWarnings}`);
  const addressing = formatAddressingDecision(payload.addressing, payload.presence);
  if (addressing !== "-") parts.push(addressing);
  const command = formatCommandDecision(payload.command);
  if (command !== "-") parts.push(command);
  if (payload.delivery_type) parts.push(String(payload.delivery_type));
  const relationship = payload.relationship || {};
  if (relationship.state?.level) parts.push(`关系:${relationshipLevelLabel(relationship.state.level)}`);
  const profile = payload.user_profile || {};
  const profileSummary = formatUserProfileDecision(profile);
  if (profileSummary !== "-") parts.push(profileSummary);
  const promptContext = payload.prompt_context || {};
  const promptContextSummary = formatPromptContextDecision(promptContext);
  if (promptContextSummary !== "-") parts.push(promptContextSummary);
  const emotion = payload.emotion || {};
  const emotionSummary = formatEmotionDecision(emotion);
  if (emotionSummary !== "-") parts.push(emotionSummary);
  const memory = payload.memory_recall || {};
  const memorySummary = formatMemoryRecallDecision(memory);
  if (memorySummary !== "-") parts.push(memorySummary);
  const novelty = payload.reply_novelty || {};
  if (novelty.action && novelty.action !== "keep") parts.push(`复读:${noveltyActionLabel(novelty.action)}`);
  const sticker = payload.sticker_decision || {};
  if (sticker.has_plan) parts.push(`表情:${stickerActionLabel(sticker.action)}/${stickerResultLabel(sticker.result || sticker.failure_reason)}`);
  const taboo = payload.taboo || {};
  if (taboo.matched) parts.push(`禁区:${tabooActionLabel(taboo.action)}`);
  const multimodal = payload.multimodal || {};
  const multimodalSummary = formatMultimodalDecision(multimodal);
  if (multimodalSummary !== "-") parts.push(multimodalSummary);
  const burstMerge = payload.burst_merge || {};
  const burstMergeSummary = formatBurstMergeDecision(burstMerge);
  if (burstMergeSummary !== "-") parts.push(burstMergeSummary);
  return parts.length ? parts.join(" / ") : "-";
}
function formatMessageDiagnosis(row: Record<string, any>) {
  const decision = row.trace_final_decision && typeof row.trace_final_decision === "object"
    ? row.trace_final_decision as Record<string, any>
    : {};
  const parts: string[] = [];
  if (row.status && row.status !== "ok") parts.push(messageStatusLabel(row.status));
  const pipelineError = formatPipelineError((row.reply_delivery || {}).pipeline_error || decision.pipeline_error);
  if (pipelineError !== "-") parts.push(`异常:${pipelineError}`);
  const pipelineWarnings = formatPipelineWarnings((row.reply_delivery || {}).pipeline_warnings || decision.pipeline_warnings);
  if (pipelineWarnings !== "-") parts.push(`降级:${pipelineWarnings}`);
  const addressing = formatAddressingDecision(decision.addressing, decision.presence);
  if (addressing !== "-") parts.push(addressing);
  const command = formatCommandDecision(decision.command);
  if (command !== "-") parts.push(command);
  const burst = formatBurstMergeDecision(decision.burst_merge);
  if (burst !== "-") parts.push(burst);
  return parts.length ? parts.join(" / ") : row.is_at_me ? "@" : "-";
}
function formatCommandDecision(value: unknown) {
  const payload = value && typeof value === "object" ? value as Record<string, any> : {};
  const command = String(payload.command || "").trim();
  if (!command) return "-";
  const outcome = String(payload.outcome || "").trim();
  const labelMap: Record<string, string> = {
    handled: "已处理",
    error: "异常",
    denied: "已拒绝",
    unknown_replied: "未知已提示",
    unknown_silent: "未知静默",
    dry_run: "Dry Run",
    received: "已接收"
  };
  const parts = [`命令:${command} ${labelMap[outcome] || outcome || "未知"}`];
  if (payload.scope) parts.push(`scope=${payload.scope}`);
  if (payload.args_present) parts.push("有参数");
  if (payload.suggestion) parts.push(`建议=${payload.suggestion}`);
  if (payload.denial_reason) parts.push(`拒绝=${payload.denial_reason}`);
  if (payload.error_type) parts.push(`异常=${payload.error_type}`);
  return parts.join(" ");
}
function formatPipelineError(value: unknown) {
  const payload = value && typeof value === "object" ? value as Record<string, any> : {};
  if (!Object.keys(payload).length) return "-";
  const stage = String(payload.stage || "unknown");
  const errorType = String(payload.error_type || "Error");
  return `${stage}/${errorType}`;
}
function formatPipelineWarnings(value: unknown) {
  const warnings = Array.isArray(value) ? value : [];
  const labels = warnings.slice(0, 3).map((item) => {
    const payload = item && typeof item === "object" ? item as Record<string, any> : {};
    const stage = String(payload.stage || "unknown");
    const errorType = String(payload.error_type || "Error");
    return `${stage}/${errorType}`;
  }).filter(Boolean);
  return labels.length ? labels.join(", ") : "-";
}
function formatAddressingDecision(addressingValue: unknown, presenceValue: unknown) {
  const addressing = addressingValue && typeof addressingValue === "object" ? addressingValue as Record<string, any> : {};
  const presence = presenceValue && typeof presenceValue === "object" ? presenceValue as Record<string, any> : {};
  const parts: string[] = [];
  const mentionOnly = Boolean(addressing.mention_only);
  const mentionOnlyKind = String(addressing.mention_only_kind || "");
  const noticeKind = noticeOriginKind(addressing);
  if (noticeKind === "poke" || mentionOnlyKind === "poke") {
    parts.push("戳一戳");
  } else if (addressing.social_ping && noticeKind) {
    parts.push(`社交ping(${noticeKind})`);
  } else if (mentionOnly && mentionOnlyKind && !["at", "name"].includes(mentionOnlyKind)) {
    parts.push(`只唤醒(${mentionOnlyKind})`);
  }
  if (addressing.at_mention || presence.observe_wakeup === "at_mention") {
    parts.push(mentionOnly && mentionOnlyKind === "at" ? "只@" : "@");
  }
  if (addressing.name_mention || presence.observe_wakeup === "name_mention") {
    const label = mentionOnly && mentionOnlyKind === "name" ? "只喊名" : "喊名";
    parts.push(addressing.name_alias ? `${label}(${addressing.name_alias})` : label);
  }
  return parts.length ? parts.join("+") : "-";
}
function noticeOriginKind(addressing: Record<string, any>) {
  const origin = addressing.notice_origin && typeof addressing.notice_origin === "object"
    ? addressing.notice_origin as Record<string, any>
    : {};
  const kind = String(origin.kind || "").trim();
  const noticeType = String(origin.notice_type || "").trim();
  const subType = String(origin.sub_type || "").trim();
  if (kind) return kind;
  if (noticeType === "poke" || (noticeType === "notify" && subType === "poke")) return "poke";
  return noticeType || subType;
}
function formatTraceDeliveryTiming(value: unknown) {
  const plans = Array.isArray(value) ? value : [];
  const last = plans.length ? plans[plans.length - 1] : null;
  if (!last || typeof last !== "object") return "-";
  const payload = last as Record<string, any>;
  const parts = [
    payload.priority,
    payload.status,
    payload.estimated_delay_ms !== undefined ? `预计${Math.round(Number(payload.estimated_delay_ms) || 0)}ms` : "",
    payload.actual_delay_ms !== undefined ? `实际${Math.round(Number(payload.actual_delay_ms) || 0)}ms` : "",
    payload.stale ? `stale:${payload.stale_reason || "yes"}` : ""
  ].filter(Boolean);
  return parts.length ? parts.join(" / ") : "-";
}
function formatPresenceDecision(value: unknown) {
  const payload = value && typeof value === "object" ? value as Record<string, any> : {};
  if (!Object.keys(payload).length) return "-";
  const bits = [
    `在场 p=${formatNumber(payload.probability)}`,
    `兴趣=${formatNumber(payload.interest)}`,
    payload.interrupting ? "避免插嘴" : "",
    payload.primary_reason ? `主因:${payload.primary_reason}` : ""
  ].filter(Boolean);
  return bits.join(" / ");
}
function formatRelationshipDecision(value: unknown) {
  const payload = value && typeof value === "object" ? value as Record<string, any> : {};
  if (!Object.keys(payload).length) return "-";
  const state = payload.state || {};
  const explain = payload.explain || {};
  const bits = [
    `关系 ${relationshipLevelLabel(state.level)}`,
    `证据 ${relationshipEvidenceLabel(explain.evidence_level)}`,
    explain.event_count !== undefined ? `事件${explain.event_count}` : "",
    explain.net_delta !== undefined ? `净值${formatSigned(explain.net_delta)}` : "",
    payload.simulated_override ? "临时覆盖" : ""
  ].filter(Boolean);
  return bits.length ? bits.join(" / ") : "-";
}
function formatUserProfileDecision(value: unknown) {
  const payload = value && typeof value === "object" ? value as Record<string, any> : {};
  if (!Object.keys(payload).length || !payload.available) return "-";
  const quality = payload.quality || {};
  const evidence = payload.evidence || {};
  const traits = Array.isArray(payload.traits) && payload.traits.length
    ? ` / ${payload.traits.slice(0, 3).join("+")}`
    : "";
  const bits = [
    `画像 ${profileRelationshipLabel(quality.relationship)}`,
    `conf=${formatNumber(quality.confidence)}`,
    evidence.evidence_level ? `证据=${profileEvidenceLabel(evidence.evidence_level)}` : "",
    payload.prompt_applied ? "Prompt" : "",
    traits
  ].filter(Boolean);
  return bits.join(" / ");
}
function formatPromptContextDecision(value: unknown) {
  const payload = value && typeof value === "object" ? value as Record<string, any> : {};
  if (!Object.keys(payload).length) return "-";
  const messages = Number(payload.message_count ?? 0);
  const sections = Number(payload.section_count ?? 0);
  const applied = Array.isArray(payload.applied_sections)
    ? payload.applied_sections.slice(0, 3).join("+")
    : "";
  return `Prompt:${messages}条/${sections}块${applied ? `/${applied}` : ""}`;
}
function formatEmotionDecision(value: unknown) {
  const payload = value && typeof value === "object" ? value as Record<string, any> : {};
  if (!Object.keys(payload).length) return "-";
  const state = payload.state || {};
  const style = payload.style || {};
  const label = String(style.response_style || "").replace(/^当前情绪:\s*/, "") || "-";
  const token = payload.max_tokens?.delta !== undefined ? `Token${formatSigned(payload.max_tokens.delta)}` : "";
  const bits = [
    `情绪 ${label}`,
    `v=${formatNumber(state.valence)}`,
    `a=${formatNumber(state.arousal)}`,
    payload.prompt_applied ? "Prompt" : "",
    payload.style_applied ? "风格" : "",
    token
  ].filter(Boolean);
  return bits.join(" / ");
}
function formatMemoryRecallDecision(value: unknown) {
  const payload = value && typeof value === "object" ? value as Record<string, any> : {};
  if (!Object.keys(payload).length) return "-";
  if (!payload.used && !payload.hit_count) return "-";
  const top = Array.isArray(payload.top_results) && payload.top_results.length ? payload.top_results[0] : {};
  const source = Array.isArray(payload.retrieval_sources) && payload.retrieval_sources.length
    ? payload.retrieval_sources.slice(0, 3).join("+")
    : "";
  const preview = top?.preview ? ` / ${top.preview}` : "";
  const bits = [
    `RAG ${payload.hit_count || 0}`,
    `分=${formatNumber(payload.top_score)}`,
    source,
    preview
  ].filter(Boolean);
  return bits.join(" / ");
}
function formatReplyNoveltyDecision(value: unknown) {
  const payload = value && typeof value === "object" ? value as Record<string, any> : {};
  if (!Object.keys(payload).length) return "-";
  const score = Number(payload.max_similarity);
  const scoreText = Number.isFinite(score) ? score.toFixed(2) : "-";
  return `复读 ${noveltyActionLabel(payload.action)} / ${noveltyReasonLabel(payload.reason)} / ${scoreText}`;
}
function formatStickerDecision(value: unknown) {
  const payload = value && typeof value === "object" ? value as Record<string, any> : {};
  if (!payload.has_plan) return "-";
  const selected = payload.selected_candidate?.summary ? ` / ${payload.selected_candidate.summary}` : "";
  const reason = payload.reason ? ` / ${payload.reason}` : "";
  return `表情 ${stickerActionLabel(payload.action)} / ${stickerResultLabel(payload.result || payload.failure_reason)} / 候选${payload.candidate_count || 0}${reason}${selected}`;
}
function formatTabooDecision(value: unknown) {
  const payload = value && typeof value === "object" ? value as Record<string, any> : {};
  if (!payload.matched) return "-";
  const index = Number(payload.matched_index);
  const rule = Number.isFinite(index) ? `#${index + 1}` : "#?";
  const keywords = Array.isArray(payload.matched_keywords) && payload.matched_keywords.length
    ? ` / ${payload.matched_keywords.join("+")}`
    : "";
  return `禁区 ${rule} / ${tabooActionLabel(payload.action)}${keywords}`;
}
function formatMultimodalDecision(value: unknown) {
  const payload = value && typeof value === "object" ? value as Record<string, any> : {};
  const items = Object.entries(payload).map(([channel, detail]) => {
    const data = detail && typeof detail === "object" ? detail as Record<string, any> : {};
    if (!data.status) return "";
    return `${multimodalChannelLabel(channel)}:${multimodalStatusLabel(data.status)}`;
  }).filter(Boolean);
  return items.length ? items.join(" / ") : "-";
}
function formatBurstMergeDecision(value: unknown) {
  const payload = value && typeof value === "object" ? value as Record<string, any> : {};
  const action = String(payload.action || "");
  if (!action || action === "single") return "-";
  const count = Number(payload.message_count || 0);
  const base = action === "merged_into_pending"
    ? "连发:并入上一条"
    : action === "merged"
      ? `连发:合并${count || "?"}条`
      : `连发:${action}`;
  const hints: string[] = [];
  if (payload.contains_at_mention) hints.push("@");
  if (payload.contains_name_mention) {
    hints.push(payload.name_alias ? `喊名(${payload.name_alias})` : "喊名");
  }
  return hints.length ? `${base}/${hints.join("+")}` : base;
}
function noveltyActionLabel(value: unknown) {
  const labels: Record<string, string> = { keep: "保留", warn: "警告", skip: "跳过" };
  return labels[String(value || "")] || String(value || "-");
}
function noveltyReasonLabel(value: unknown) {
  const labels: Record<string, string> = {
    disabled: "关闭",
    too_short: "短回复",
    hard_duplicate: "硬重复",
    similar_recent_reply: "相似"
  };
  return labels[String(value || "")] || String(value || "无");
}
function stickerActionLabel(value: unknown) {
  const labels: Record<string, string> = { send: "发送", none: "不发" };
  return labels[String(value || "")] || String(value || "-");
}
function stickerResultLabel(value: unknown) {
  const labels: Record<string, string> = {
    preselected: "预召回",
    selected: "已选择",
    sent: "已发送",
    skipped: "已跳过",
    failed: "失败",
    no_candidates: "无候选",
    probability_gate: "概率门控",
    llm_none: "LLM未选",
    invalid_json: "结构化失败",
    invalid_hash: "候选无效",
    file_missing: "文件缺失"
  };
  return labels[String(value || "")] || String(value || "-");
}
function tabooActionLabel(value: unknown) {
  const labels: Record<string, string> = { deflect: "岔开", ignore: "忽略" };
  return labels[String(value || "")] || String(value || "-");
}
function multimodalChannelLabel(value: unknown) {
  const labels: Record<string, string> = { image: "图", voice: "语音" };
  return labels[String(value || "")] || String(value || "-");
}
function multimodalStatusLabel(value: unknown) {
  const labels: Record<string, string> = {
    pending: "待处理",
    recognized: "已识别",
    unrecognized: "未识别",
    unavailable: "不可用",
    collect_only: "仅收集"
  };
  return labels[String(value || "")] || String(value || "-");
}
function relationshipLevelLabel(value: unknown) {
  const labels: Record<string, string> = {
    hostile: "敌对",
    cold: "冷淡",
    neutral: "普通",
    friendly: "友好",
    close: "亲近",
    trusted: "信任"
  };
  return labels[String(value || "")] || String(value || "-");
}
function relationshipEvidenceLabel(value: unknown) {
  const labels: Record<string, string> = {
    none: "无依据",
    sparse: "依据少",
    steady: "稳定",
    rich: "充分"
  };
  return labels[String(value || "")] || String(value || "-");
}
function profileRelationshipLabel(value: unknown) {
  const labels: Record<string, string> = {
    new: "新朋友",
    acquaintance: "认识",
    familiar: "熟悉",
    old_friend: "老熟人"
  };
  return labels[String(value || "")] || String(value || "-");
}
function profileEvidenceLabel(value: unknown) {
  const labels: Record<string, string> = { none: "无", sparse: "较少", enough: "充足" };
  return labels[String(value || "")] || String(value || "-");
}
function formatSigned(value: unknown) {
  const n = Number(value);
  return Number.isFinite(n) ? `${n >= 0 ? "+" : ""}${n.toFixed(1)}` : "-";
}
function proactiveStrategyLabel(value: unknown) {
  const labels: Record<string, string> = {
    continue_topic: "接当前话题",
    comment_message: "评论近消息",
    new_topic: "开新话题"
  };
  return labels[String(value)] || String(value || "-");
}
function proactiveSendStatusLabel(value: unknown) {
  const labels: Record<string, string> = {
    sent: "已发送",
    sending: "发送中",
    dry_run: "Dry Run",
    empty_reply: "空回复",
    persona_missing: "无人设",
    error: "失败"
  };
  return labels[String(value)] || String(value || "-");
}
function proactiveSendStatusClass(value: unknown) {
  const status = String(value || "");
  if (status === "sent") return "ok";
  if (status === "error" || status === "persona_missing") return "down";
  if (status === "empty_reply") return "degraded";
  return "pending";
}
function formatProactiveDelivery(value: unknown) {
  const payload = value && typeof value === "object" ? value as Record<string, any> : {};
  const parts = [
    payload.priority,
    payload.status,
    payload.estimated_delay_ms !== undefined ? `${Math.round(Number(payload.estimated_delay_ms) || 0)}ms` : ""
  ].filter(Boolean);
  return parts.length ? parts.join(" / ") : "-";
}
function formatTime(ts: unknown) {
  const n = Number(ts);
  return Number.isFinite(n) && n > 0 ? new Date(n * 1000).toLocaleString() : "-";
}
function noReplyLevelClass(value: unknown) {
  const level = String(value || "");
  if (level === "ok") return "ok";
  if (level === "error") return "error";
  return "warning";
}
function noReplyCodeLabel(value: unknown) {
  const labels: Record<string, string> = {
    napcat_received_but_bot_missed: "OneBot 漏收",
    pipeline_or_policy_silence: "策略静默",
    reply_observed: "已观察回复",
    inbound_without_trace: "入站缺 Trace",
    no_inbound_evidence: "证据不足"
  };
  return labels[String(value || "")] || String(value || "-");
}
function optionalNumber(value: string): number | null {
  const text = String(value || "").trim();
  if (!text) return null;
  const num = Number(text);
  return Number.isFinite(num) ? num : null;
}
function fillNoReplyFromRecent() {
  const recent = (qq.value?.recent || []).filter((item: any) => item.direction === "in" && item.ts);
  const first = recent[recent.length - 1];
  const last = recent[0];
  if (!first || !last) {
    actionResult.value = "暂无最近入站消息可填充";
    return;
  }
  noReplyForm.value.since = localTimeFromEpochSeconds(Number(first.ts), -60);
  noReplyForm.value.until = localTimeFromEpochSeconds(Number(last.ts), 60);
  noReplyForm.value.group_id = first.group_id ? String(first.group_id) : "";
  noReplyForm.value.user_id = first.user_id ? String(first.user_id) : "";
  noReplyForm.value.contains = first.text_preview || "";
}
async function diagnoseNoReplyMessage(row: Record<string, any>) {
  const ts = Number(row.ts);
  if (row.direction !== "in" || !Number.isFinite(ts) || ts <= 0) {
    actionResult.value = "只能复盘带时间的入站消息";
    return;
  }
  noReplyForm.value.since = localTimeFromEpochSeconds(ts, -90);
  noReplyForm.value.until = localTimeFromEpochSeconds(ts, 90);
  noReplyForm.value.group_id = row.group_id ? String(row.group_id) : "";
  noReplyForm.value.user_id = row.user_id ? String(row.user_id) : "";
  noReplyForm.value.contains = String(row.text_preview || "").trim();
  await runNoReplyDiagnostic();
}
function usePastedNoReplyWindow() {
  noReplyForm.value.since = "";
  noReplyForm.value.until = "";
  actionResult.value = "诊断时将从粘贴内容推断时间窗口";
}
async function runNoReplyDiagnostic() {
  noReplyLoading.value = true;
  error.value = "";
  try {
    const body: NoReplyDiagnosticRequest = {
      since: noReplyForm.value.since,
      until: noReplyForm.value.until,
      group_id: optionalNumber(noReplyForm.value.group_id),
      user_id: optionalNumber(noReplyForm.value.user_id),
      contains: noReplyForm.value.contains.trim(),
      include_napcat: noReplyForm.value.include_napcat,
      napcat_log_text: noReplyForm.value.napcat_log_text,
      db_path: "data/app.db"
    };
    const payload = await postJson<NoReplyDiagnosticPayload>("/api/diagnostics/no-reply", body);
    noReplyReport.value = payload.report;
    actionResult.value = `不回复复盘：${noReplyCodeLabel(payload.report.diagnosis.code)}`;
  } catch (err: any) {
    error.value = err?.message || "不回复复盘失败";
  } finally {
    noReplyLoading.value = false;
  }
}
async function copyNoReplyReport() {
  if (!noReplyReport.value) return;
  await copyNoReplyJson(noReplyReport.value, "不回复诊断已复制");
}
function focusNoReplyTrace() {
  const traceId = noReplyTraceId.value;
  if (!traceId) return;
  traceQuery.value = traceId;
  const fullTrace = (dashboard.value?.traces || []).find((item: any) => item.trace_id === traceId);
  selectedTrace.value = fullTrace || noReplyTraceFallback.value || null;
  actionResult.value = fullTrace ? "已定位诊断 Trace" : "已筛选诊断 Trace；完整详情可能不在当前 dashboard 快照中";
}
function setTimer() {
  if (timer) window.clearInterval(timer);
  timer = autoRefresh.value ? window.setInterval(() => load(true), 8000) : undefined;
}
async function load(silent = false) {
  if (!silent) {
    loading.value = true;
    error.value = "";
    actionResult.value = "";
  }
  try {
    dashboard.value = await api<DashboardPayload>("/api/monitoring/dashboard");
    lastLoadedAt.value = new Date();
    if (!selectedTrace.value || !filteredTraces.value.some((item: any) => item.trace_id === selectedTrace.value?.trace_id)) {
      selectedTrace.value = filteredTraces.value[0] || null;
    }
    if (!silent) actionResult.value = "监控数据已刷新";
  } catch (err) {
    if (!silent) {
      error.value = err instanceof Error ? err.message : "监控数据加载失败";
      toast.push(error.value, "error");
    }
  } finally {
    if (!silent) loading.value = false;
  }
}
function toggleAutoRefresh() {
  autoRefresh.value = !autoRefresh.value;
  setTimer();
  actionResult.value = autoRefresh.value ? "已开启自动刷新" : "已暂停自动刷新";
}
async function copySnapshot() {
  const payload = {
    generated_at: dashboard.value?.generated_at || 0,
    copied_at: new Date().toISOString(),
    health: dashboard.value?.health || {},
    security: dashboard.value?.security || {},
    onebot: onebotRuntime.value,
    qq: qq.value,
    delivery_summary: delivery.value?.summary || {},
    learning_summary: learning.value?.summary || {},
    diagnostics: diagnostics.value,
    filtered: {
      delivery: filteredDeliveryPlans.value,
      messages: filteredMessages.value.slice(0, 30),
      traces: filteredTraces.value.slice(0, 10)
    }
  };
  await copySnapshotJson(payload, "监控诊断快照已复制");
}
async function copyTrace(trace: any) {
  await copyTraceJson(trace, "Trace JSON 已复制");
}
onMounted(() => { load(); setTimer(); });
onBeforeUnmount(() => { if (timer) window.clearInterval(timer); });
</script>

<style scoped>
.runtime-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 10px;
}
.runtime-grid div {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 10px;
}
.runtime-grid span {
  display: block;
  color: var(--muted);
  font-size: 12px;
}
.runtime-grid strong {
  display: block;
  margin-top: 6px;
  font-size: 18px;
}
.runtime-grid.compact {
  margin-bottom: 12px;
}
.compact-actions {
  justify-content: flex-start;
  margin-bottom: 12px;
}
.runtime-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 12px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
}
.runtime-summary strong {
  flex: 0 0 auto;
}
.runtime-summary span {
  color: var(--muted);
  line-height: 1.5;
}
.runtime-summary.ok {
  border-color: rgba(33, 150, 83, 0.4);
}
.runtime-summary.warning {
  border-color: rgba(245, 158, 11, 0.5);
}
.runtime-summary.error {
  border-color: rgba(239, 68, 68, 0.5);
}
.runtime-preview {
  margin: 10px 0 0;
  color: var(--muted);
  word-break: break-word;
}
.warning-text {
  color: var(--warning);
}

.toolbar input {
  min-width: min(360px, 100%);
  flex: 1 1 280px;
}

.save-result {
  border: 1px solid #386f47;
  background: #142016;
  color: var(--success);
  border-radius: 8px;
  padding: 10px 12px;
}

.diagnostic-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 10px;
}

.diagnostic-row {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 10px 12px;
}

.diagnostic-row strong,
.diagnostic-row span {
  display: block;
}

.diagnostic-row span {
  color: var(--muted);
  margin-top: 5px;
}

.diagnostic-row.ok {
  border-color: #386f47;
}

.diagnostic-row.warning {
  border-color: #78612d;
}

.diagnostic-row.error {
  border-color: #743434;
}

.row-actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.reason-cell {
  display: grid;
  gap: 4px;
  min-width: 150px;
}

.reason-cell small {
  color: var(--muted);
  font-size: 12px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.trace-detail {
  display: grid;
  gap: 12px;
}

.trace-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.trace-pills span {
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--panel-2);
  color: var(--text);
  font-size: 13px;
  line-height: 1.45;
  padding: 5px 8px;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.detail-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.detail-head strong {
  word-break: break-all;
}
</style>
